// =============================================================================
// PRODUCTS SERVICE
// =============================================================================
// Service for fetching products from Firestore

import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { firestore, isFirebaseConfigured } from './firestore';
import type { Product, ProductCategory } from '@/types/products';

class ProductsService {
  private readonly COLLECTION_NAME = 'products';

  /**
   * Get all active products from Firestore
   */
  async getAllProducts(): Promise<Product[]> {
    if (!isFirebaseConfigured || !firestore) {
      console.warn('Firebase not configured, returning empty products array');
      return [];
    }

    try {
      const productsRef = collection(firestore, this.COLLECTION_NAME);
      const q = query(
        productsRef,
        where('isActive', '==', true),
        orderBy('displayOrder', 'asc'),
        orderBy('name', 'asc')
      );

      const snapshot = await getDocs(q);
      const products: Product[] = [];

      snapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        products.push({
          id: docSnapshot.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          variants: (data.variants || []).map((variant: any) => ({
            ...variant,
            createdAt: variant.createdAt?.toDate() || new Date(),
            updatedAt: variant.updatedAt?.toDate() || new Date(),
          })),
        } as Product);
      });

      return products;
    } catch (error) {
      console.error('Error fetching products from Firestore:', error);
      return [];
    }
  }

  /**
   * Get a single product by ID
   */
  async getProductById(productId: string): Promise<Product | null> {
    if (!isFirebaseConfigured || !firestore) {
      console.warn('Firebase not configured');
      return null;
    }

    try {
      const productRef = doc(firestore, this.COLLECTION_NAME, productId);
      const productSnap = await getDoc(productRef);

      if (!productSnap.exists()) {
        return null;
      }

      const data = productSnap.data();
      return {
        id: productSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        variants: (data.variants || []).map((variant: any) => ({
          ...variant,
          createdAt: variant.createdAt?.toDate() || new Date(),
          updatedAt: variant.updatedAt?.toDate() || new Date(),
        })),
      } as Product;
    } catch (error) {
      console.error('Error fetching product by ID:', error);
      return null;
    }
  }

  /**
   * Get products by category
   */
  async getProductsByCategory(category: string): Promise<Product[]> {
    if (!isFirebaseConfigured || !firestore) {
      return [];
    }

    try {
      const productsRef = collection(firestore, this.COLLECTION_NAME);
      const q = query(
        productsRef,
        where('category', '==', category),
        where('isActive', '==', true),
        orderBy('displayOrder', 'asc')
      );

      const snapshot = await getDocs(q);
      const products: Product[] = [];

      snapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        products.push({
          id: docSnapshot.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          variants: (data.variants || []).map((variant: any) => ({
            ...variant,
            createdAt: variant.createdAt?.toDate() || new Date(),
            updatedAt: variant.updatedAt?.toDate() || new Date(),
          })),
        } as Product);
      });

      return products;
    } catch (error) {
      console.error('Error fetching products by category:', error);
      return [];
    }
  }

  /**
   * Convert Product to PricingSection format for compatibility
   */
  convertProductToPricingFormat(product: Product): any {
    return {
      name: product.name,
      montageImage: product.montageImage,
      description: product.description,
      sizes: product.sizes,
      features: product.features,
      products: product.variants.map((variant) => ({
        image: variant.image,
        price: variant.priceDisplay,
      })),
      popular: product.isPopular,
    };
  }
}

export const productsService = new ProductsService();
export default productsService;

